import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Container, Form, Modal, Spinner, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { MEMO_STATUS, UPLOAD_BASE, beYearOptions, fmtDateTimeTH } from './aoShared'

// ธุรการ: รับเรื่องบันทึกข้อความ ออกเลข และเสนอแฟ้มผู้บริหาร
export default function MemoScreening() {
  const [status, setStatus] = useState('submitted')
  const [year, setYear] = useState('')
  const [q, setQ] = useState('')
  const [memos, setMemos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [viewMemo, setViewMemo] = useState(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (status) params.set('status', status)
      if (year) params.set('year', year)
      if (q) params.set('q', q)
      const res = await axiosInstance.get(`/api/ao/memos?${params.toString()}`)
      if (res.data.success) setMemos(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [status, year, q])

  useEffect(() => { fetchData() }, [fetchData])

  const handlePropose = async (m) => {
    const result = await Swal.fire({
      icon: 'question',
      title: 'ออกเลขและเสนอแฟ้มผู้บริหาร?',
      text: `${m.mm_category} — ${m.mm_subject} (จาก ${m.created_by_name})`,
      showCancelButton: true,
      confirmButtonText: 'เสนอแฟ้ม',
      cancelButtonText: 'ปิด',
    })
    if (!result.isConfirmed) return
    try {
      const res = await axiosInstance.put(`/api/ao/memos/${m.mm_id}/propose`)
      Swal.fire({ icon: 'success', title: `ออกเลข ${res.data.data.mm_full_no} และเสนอแฟ้มเรียบร้อย`, timer: 2000, showConfirmButton: false })
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เสนอแฟ้มไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-folder-received-line me-2 text-primary" />
              รับเรื่อง / เสนอแฟ้มผู้บริหาร
            </h4>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          <Card className="card-one">
            <Card.Header className="d-flex align-items-center gap-2 flex-wrap">
              <Form.Select size="sm" style={{ width: 170 }} value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">ทุกสถานะ</option>
                {Object.entries(MEMO_STATUS).filter(([k]) => k !== 'draft').map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </Form.Select>
              <Form.Select size="sm" style={{ width: 120 }} value={year} onChange={e => setYear(e.target.value)}>
                <option value="">ทุกปี</option>
                {beYearOptions().map(y => <option key={y} value={y}>พ.ศ. {y}</option>)}
              </Form.Select>
              <Form.Control size="sm" style={{ maxWidth: 240 }} placeholder="ค้นหา เรื่อง/ผู้เขียน/เลขบันทึก"
                value={q} onChange={e => setQ(e.target.value)} />
              <div className="ms-auto text-muted fs-sm">{memos.length} รายการ</div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : memos.length === 0 ? (
                <div className="text-center text-muted py-4">ไม่มีบันทึกข้อความในเงื่อนไขที่เลือก</div>
              ) : (
                <Table responsive hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 90 }}>เลขบันทึก</th>
                      <th>เรื่อง</th>
                      <th style={{ width: 150 }}>ผู้เขียน</th>
                      <th style={{ width: 140 }}>วันที่ส่ง</th>
                      <th style={{ width: 130 }}>สถานะ</th>
                      <th style={{ width: 170 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {memos.map(m => {
                      const st = MEMO_STATUS[m.mm_status] || MEMO_STATUS.submitted
                      return (
                        <tr key={m.mm_id}>
                          <td>{m.mm_full_no || '-'}</td>
                          <td>
                            <div className="fw-semibold">{m.mm_subject}</div>
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>{m.mm_category}</div>
                            {m.mm_exec_comment && (
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>ความเห็นผู้บริหาร: {m.mm_exec_comment}</div>
                            )}
                          </td>
                          <td>{m.created_by_name}</td>
                          <td>{fmtDateTimeTH(m.created_at)}</td>
                          <td><Badge bg={st.bg}>{st.label}</Badge></td>
                          <td className="text-end">
                            <Button size="sm" variant="outline-secondary" className="me-1" title="ดูรายละเอียด" onClick={() => setViewMemo(m)}>
                              <i className="ri-eye-line" />
                            </Button>
                            <Button as={Link} to={`/my/memos/${m.mm_id}/print`} size="sm" variant="outline-secondary" className="me-1" title="พิมพ์">
                              <i className="ri-printer-line" />
                            </Button>
                            {m.mm_status === 'submitted' && (
                              <Button size="sm" variant="primary" onClick={() => handlePropose(m)}>
                                <i className="ri-send-plane-line me-1" />เสนอแฟ้ม
                              </Button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />

      {/* View modal */}
      <Modal show={!!viewMemo} onHide={() => setViewMemo(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">{viewMemo?.mm_category} — {viewMemo?.mm_full_no || 'ยังไม่ออกเลข'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewMemo && (
            <>
              <div className="fw-semibold mb-1">เรื่อง: {viewMemo.mm_subject}</div>
              <div className="text-muted fs-sm mb-2">
                ผู้เขียน: {viewMemo.created_by_name} · {fmtDateTimeTH(viewMemo.created_at)}
              </div>
              {viewMemo.mm_body ? (
                <div className="border rounded p-3" dangerouslySetInnerHTML={{ __html: viewMemo.mm_body }} />
              ) : (
                <div className="text-muted fs-sm">— ไม่มีเนื้อหา —</div>
              )}
              {viewMemo.mm_file && (
                <div className="mt-2">
                  <a href={`${UPLOAD_BASE}/uploads/ao_docs/${viewMemo.mm_file}`} target="_blank" rel="noreferrer">
                    <i className="ri-attachment-2 me-1" />เปิดไฟล์แนบ
                  </a>
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}
